import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Alert, InputGroup, Spinner } from "react-bootstrap";
import { FaPlus, FaBarcode, FaCheck, FaTimes, FaPrint } from 'react-icons/fa';
import bwipjs from 'bwip-js';
import ImageUpload from './ImageUpload';
import AddBirlikModal from './AddBirlikModal';
import AddCategoryModal from './AddCategoryModal';
import { useAuth } from "../contexts/AuthContext";
import { toast } from 'react-toastify';

const AddProductModal = ({ show, handleClose, editProduct, handleAddProduct, handleUpdateProduct, handleSaveProduct, productData, setProductData, saving }) => {
  const { user } = useAuth();
  const [costingMethod, setCostingMethod] = useState('FIFO');
  const [categories, setCategories] = useState([]);
  const [birliklar, setBirliklar] = useState([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showBirlikModal, setShowBirlikModal] = useState(false);
  const [form, setForm] = useState({
    name: '',
    barcode: '',
    category_id: '',
    birlik: '',
    minimum_quantity: '',
    saving_term: '',
    costing_method: 'FIFO',
    description: '',
    photo: ''
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingBarcode, setCheckingBarcode] = useState(false);
  const [barcodeStatus, setBarcodeStatus] = useState(null); // 'valid' | 'invalid' | null

  const checkBarcodeAvailability = async (barcode) => {
    if (!barcode || barcode.length < 3) {
      setBarcodeStatus(null);
      return;
    }

    setCheckingBarcode(true);
    try {
      const token = localStorage.getItem('token');
      const productId = editProduct ? (editProduct.productid || editProduct.id) : '';
      const res = await fetch(`/api/products/check-barcode/${barcode}${productId ? `?productId=${productId}` : ''}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!res.ok) throw new Error('Failed to check barcode');
      const data = await res.json();

      if (data.exists) {
        setBarcodeStatus('invalid');
        setError(`Ushbu shtrix-kod allaqachon mavjud: ${data.productName}`);
      } else {
        setBarcodeStatus('valid');
        setError(null);
      }
    } catch (err) {
      console.error('Error checking barcode:', err);
    } finally {
      setCheckingBarcode(false);
    }
  };

  const generateBarcode = () => {
    // Generate 12-digit random number (suitable for Subset-C)
    const randomDigits = Math.floor(100000000000 + Math.random() * 900000000000).toString();
    setForm(f => ({ ...f, barcode: randomDigits }));
    checkBarcodeAvailability(randomDigits);
  };

  const handlePrintBarcode = () => {
    if (!form.barcode) {
      toast.error('Shtrix-kod kiritilmagan!');
      return;
    }

    try {
      // Generate SVG string using bwipjs
      const svgString = bwipjs.toSVG({
        bcid: 'code128',       // Barcode type
        text: form.barcode,    // Text to encode
        scale: 3,              // Scaling factor
        height: 12,            // Bar height, in millimeters
        includetext: true,     // Show human-readable text
        textxalign: 'center',  // Centered text
        paddingleft: 10,       // Quiet zone left
        paddingright: 10,      // Quiet zone right
      });

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error('Popup bloklangan! Iltimos, popup-larga ruxsat bering.');
        return;
      }

      printWindow.document.open();
      printWindow.document.write(`
        <html>
          <head>
            <title>Shtrix-kod: ${form.barcode}</title>
            <style>
              @page {
                margin: 0;
                size: auto;
              }
              body {
                margin: 0;
                padding: 10px;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                font-family: Arial, sans-serif;
              }
              .barcode-container {
                display: flex;
                flex-direction: column;
                align-items: center;
                text-align: center;
                width: 100%;
              }
              svg {
                max-width: 100%;
                height: auto;
              }
            </style>
          </head>
          <body>
            <div class="barcode-container">
              ${svgString}
            </div>
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                  window.close();
                }, 500);
              };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    } catch (e) {
      console.error('Barcode generation error:', e);
      toast.error('Shtrix-kod generatorida xatolik yuz berdi');
    }
  };

  const fetchCategories = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch('/api/categories', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!res.ok) throw new Error('Failed to fetch categories');
      const data = await res.json();
      const items = Array.isArray(data) ? data : (data.items || []);
      setCategories(items);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setCategories([]);
    }
  };

  const fetchBirliklar = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch('/api/birlik', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!res.ok) throw new Error('Failed to fetch birliklar');
      const data = await res.json();
      const items = Array.isArray(data) ? data : (data.items || []);
      setBirliklar(items);
    } catch (err) {
      console.error('Error fetching birliklar:', err);
      setBirliklar([]);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchBirliklar();
  }, []);

  useEffect(() => {
    if (editProduct) {
      setForm({
        name: editProduct.name || '',
        barcode: editProduct.barcode || '',
        category_id: editProduct.category_id || '',
        birlik: editProduct.birlik || '',
        minimum_quantity: editProduct.minimum_quantity || '',
        saving_term: editProduct.saving_term || '',
        costing_method: editProduct.costing_method || 'FIFO',
        description: editProduct.description || '',
        photo: editProduct.photo || '',
      });
      setCostingMethod(editProduct.costing_method || 'FIFO');
    } else {
      setForm({
        name: '',
        barcode: '',
        category_id: '',
        birlik: '',
        minimum_quantity: '',
        saving_term: '',
        costing_method: 'FIFO',
        description: '',
        photo: '',
      });
      setCostingMethod('FIFO');
    }
  }, [editProduct, show]);

  useEffect(() => {
    if (costingMethod !== 'FIFO' || !form.barcode || form.barcode.length < 3) {
      setBarcodeStatus(null);
      return;
    }

    const timer = setTimeout(() => {
      checkBarcodeAvailability(form.barcode);
    }, 600);

    return () => clearTimeout(timer);
  }, [form.barcode, costingMethod]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (name === 'costing_method') {
      setCostingMethod(value);
      // Reset barcode for Specific Identification
      if (value === 'Specific') {
        setForm(f => ({ ...f, barcode: '' }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setLoading(true);

    // Validation
    if (!form.name || !form.costing_method) {
      setError("Majburiy maydonlarni to'ldiring!");
      return;
    }

    if (costingMethod === 'FIFO' && !form.barcode) {
      setError("FIFO usuli uchun shtrix-kod majburiy!");
      return;
    }

    if (costingMethod === 'FIFO' && barcodeStatus === 'invalid') {
      setError("Ushbu shtrix-kod allaqachon mavjud! Boshqa kod kiriting.");
      return;
    }

    if (checkingBarcode) {
      setError("Shtrix-kod tekshirilmoqda, iltimos kuting...");
      return;
    }

    try {
      let payload = { ...form };
      payload.minimum_quantity = Number(form.minimum_quantity) || 0;

      // For Specific Identification, barcode should be empty
      if (costingMethod === 'Specific') {
        payload.barcode = '';
      }

      // Ensure photo is included in payload if it exists
      if (form.photo) {
        payload.photo = form.photo;
      } else {
        payload.photo = '';
      }

      // Use the parent's handlers instead of making direct API calls
      if (editProduct) {
        await handleUpdateProduct(payload);
      } else {
        await handleAddProduct(payload);
      }

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      setError(err.message || 'Xatolik yuz berdi');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (imagePath) => {
    setForm(f => ({ ...f, photo: imagePath }));
  };

  const handleBirlikSave = async (newBirlik) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch('/api/birlik', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Owner-Id': String(user?.ownerId || user?.id || ''),
          'X-Staff-Id': String(user?.staffId ?? '')
        },
        body: JSON.stringify({
          ...newBirlik,
          owner_id: user.role === 'owner' ? user.id : user.owner_id
        })
      });

      if (!response.ok) throw new Error('Birlik qo\'shib bo\'lmadi');

      const savedBirlik = await response.json();
      toast.success('Birlik muvaffaqiyatli qo\'shildi');
      await fetchBirliklar();
      setForm(f => ({ ...f, birlik: savedBirlik.id }));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleCategorySuccess = async (newCategory) => {
    await fetchCategories();
    setForm(f => ({ ...f, category_id: newCategory.id }));
  };

  const renderCategories = () => {
    try {
      if (!Array.isArray(categories) || categories.length === 0) {
        return <option disabled>Kategoriyalar yuklanmoqda...</option>;
      }
      return categories.map(category => (
        <option key={category.id} value={category.id}>
          {category.name}
        </option>
      ));
    } catch (error) {
      console.error('Error rendering categories:', error);
      return <option disabled>Kategoriyalarni yuklab bo'lmadi</option>;
    }
  };

  const renderBirliklar = () => {
    try {
      if (!Array.isArray(birliklar) || birliklar.length === 0) {
        return <option disabled>Birliklar yuklanmoqda...</option>;
      }
      return birliklar.map(birlik => (
        <option key={birlik.id} value={birlik.id}>
          {birlik.name}
        </option>
      ));
    } catch (error) {
      console.error('Error rendering birliklar:', error);
      return <option disabled>Birliklarni yuklab bo'lmadi</option>;
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" scrollable backdrop="static" dialogClassName="border border-3 border-dark shadow-lg rounded-3">
      <Modal.Header closeButton>
        <Modal.Title>{editProduct ? 'Mahsulotni tahrirlash' : "Qo'shish"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{editProduct ? 'Mahsulot yangilandi!' : 'Mahsulot yaratildi!'}</Alert>}
        <Form id="addProductForm" onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="productName">
                <Form.Label>Nom*</Form.Label>
                <Form.Control type="text" placeholder="Nom kiriting" name="name" value={form.name} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={6}>
              {costingMethod === 'FIFO' && (
                <Form.Group controlId="barcode" className="position-relative">
                  <Form.Label>Shtrix-kod*</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Shtrix-kod kiriting"
                      name="barcode"
                      value={form.barcode}
                      onChange={handleChange}
                      isInvalid={barcodeStatus === 'invalid'}
                      isValid={barcodeStatus === 'valid'}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={generateBarcode}
                      title="Shtrix-kod yaratish"
                    >
                      <FaBarcode />
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={handlePrintBarcode}
                      title="Shtrix-kodni chop etish"
                      disabled={!form.barcode}
                    >
                      <FaPrint />
                    </Button>
                  </InputGroup>
                  <div className="position-absolute top-50 translate-middle-y mt-2 pe-2" style={{ zIndex: 5, right: '95px' }}>
                    {checkingBarcode && <Spinner animation="border" size="sm" variant="primary" />}
                  </div>
                </Form.Group>
              )}
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="category_id">
                <Form.Label>Kategoriya</Form.Label>
                <InputGroup>
                  <Form.Select name="category_id" value={form.category_id} onChange={handleChange}>
                    <option value="">Tanlang...</option>
                    {renderCategories()}
                  </Form.Select>
                  <Button variant="outline-success" onClick={() => setShowCategoryModal(true)}>
                    <FaPlus />
                  </Button>
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="birlik">
                <Form.Label>Birlik</Form.Label>
                <InputGroup>
                  <Form.Select name="birlik" value={form.birlik} onChange={handleChange}>
                    <option value="">Tanlang...</option>
                    {renderBirliklar()}
                  </Form.Select>
                  <Button variant="outline-success" onClick={() => setShowBirlikModal(true)}>
                    <FaPlus />
                  </Button>
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="minimum_quantity">
                <Form.Label>Minimal zaxira</Form.Label>
                <Form.Control type="number" placeholder="Minimal zaxira" name="minimum_quantity" value={form.minimum_quantity} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="saving_term">
                <Form.Label>Saqlash muddati (ixtiyoriy)</Form.Label>
                <Form.Control type="date" name="saving_term" value={form.saving_term} onChange={handleChange} />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="costingMethod">
                <Form.Label>Hisoblash usuli*</Form.Label>
                <Form.Select
                  name="costing_method"
                  value={costingMethod}
                  onChange={handleChange}
                  disabled={Boolean(editProduct)}
                >
                  <option value="FIFO">FIFO (Birinci kirgan birinchi chiqadi)</option>
                  <option value="Specific">Specific Identification (Partiya/seriya bo'yicha)</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="imageUpload">
                <Form.Label>Rasm (ixtiyoriy)</Form.Label>
                <ImageUpload
                  onUpload={handleImageUpload}
                  initialImage={form.photo}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="description">
                <Form.Label>Tavsif (ixtiyoriy)</Form.Label>
                <Form.Control as="textarea" rows={3} placeholder="Tavsif kiriting" name="description" value={form.description} onChange={handleChange} />
              </Form.Group>
            </Col>
          </Row>
          {costingMethod === 'Specific' && (
            <Alert variant="info">
              <strong>Eslatma:</strong> Specific Identification usuli uchun mahsulot zaxirasi partiya/seriya bo'yicha boshqariladi.
              Mahsulot yaratilgandan so'ng, xaridlar orqali partiyalar qo'shishingiz mumkin.
            </Alert>
          )}
          {costingMethod === 'FIFO' && (
            <Alert variant="info">
              <strong>Eslatma:</strong> FIFO usuli uchun mahsulot zaxirasi ham partiya bo'yicha boshqariladi.
              Mahsulot yaratilgandan so'ng, xaridlar orqali zaxira qo'shishingiz mumkin.
            </Alert>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading || saving}>
          Bekor qilish
        </Button>
        <Button
          variant="success"
          type="submit"
          form="addProductForm"
          disabled={loading || saving}
        >
          {(loading || saving) ? 'Yuklanmoqda...' : (editProduct ? 'Saqlash' : "Qo'shish")}
        </Button>
      </Modal.Footer>

      {/* Nested Modals */}
      <AddCategoryModal
        show={showCategoryModal}
        handleClose={() => setShowCategoryModal(false)}
        onSuccess={handleCategorySuccess}
      />

      <AddBirlikModal
        show={showBirlikModal}
        onClose={() => setShowBirlikModal(false)}
        onSave={handleBirlikSave}
      />
    </Modal>
  );
};

export default AddProductModal;
